import { useEffect, useState } from 'react';
import { Modal, Button, Input, useToast } from '@bbpos/ui';
import { parseSerials } from '@/features/purchase-orders/serials';
import type { GoodsReceipt } from '@/features/purchase-orders';
import { formatDocAmount } from '@/utils/purchase-currency';
import { useCreatePurchaseReturn, usePostPurchaseReturn } from './use-purchase-returns-query';

/** What can still go back on a receipt line: in (after reversals) − already returned. Product lines only. */
export function returnableQuantity(i: GoodsReceipt['items'][number]): number {
  if (!i.product_id) return 0;
  return Math.max(0, i.quantity - (i.returned_quantity ?? 0));
}

/**
 * Return to supplier from one receipt (QA PU3-005). An OWNER posts it straight away (stock out
 * and debit note); a MANAGER saves a draft for the owner to post.
 */
export function ReturnToSupplierModal({
  poId, grn, locationName, currency, isOwner, onClose,
}: {
  poId: string;
  /** The PO's currency (the debit note is in it). */
  currency: string;
  grn: GoodsReceipt | null;
  locationName: string | null;
  isOwner: boolean;
  onClose: () => void;
}) {
  const { toast } = useToast();
  const createMutation = useCreatePurchaseReturn();
  const postMutation = usePostPurchaseReturn();
  const [qty, setQty] = useState<Record<string, string>>({});
  const [serials, setSerials] = useState<Record<string, string>>({});
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (!grn) return;
    setQty(Object.fromEntries(grn.items.map((i) => [i.id, ''])));
    setSerials({});
    setReason('');
  }, [grn]);

  const lines = grn?.items ?? [];
  const picked = lines.filter((i) => Number(qty[i.id]) > 0);
  const invalid = lines.some((i) => {
    const q = Number(qty[i.id] || 0);
    if (q < 0 || q > returnableQuantity(i)) return true;
    if (q > 0 && (i.serial_numbers?.length ?? 0) > 0) return parseSerials(serials[i.id] ?? '').length !== q;
    return false;
  });
  const busy = createMutation.isPending || postMutation.isPending;

  const submit = async () => {
    if (!grn) return;
    try {
      const created = await createMutation.mutateAsync({
        purchase_order_id: poId,
        goods_receipt_id: grn.id,
        reason: reason.trim(),
        items: picked.map((i) => ({
          goods_receipt_item_id: i.id,
          quantity: Number(qty[i.id]),
          ...((i.serial_numbers?.length ?? 0) > 0 ? { serial_numbers: parseSerials(serials[i.id] ?? '') } : {}),
        })),
      });
      if (!isOwner) {
        toast({ title: `${created.return_number} saved as a draft — the owner posts it`, variant: 'success' });
        onClose();
        return;
      }
      try {
        const posted = await postMutation.mutateAsync(created.id);
        const dn = posted.debit_note;
        toast({
          title: dn
            ? `${posted.return_number} posted — debit note ${dn.number} for ${formatDocAmount(dn.total_amount, currency)}`
            : `${posted.return_number} posted`,
          variant: 'success',
        });
      } catch (e) {
        toast({ title: `${created.return_number} is saved as a draft: ${(e as Error).message}`, variant: 'error' });
      }
      onClose();
    } catch (e) {
      toast({ title: (e as Error).message, variant: 'error' });
    }
  };

  return (
    <Modal
      open={grn != null}
      onClose={onClose}
      title={`Return to supplier — ${grn?.grn_number ?? ''}`}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>Keep</Button>
          <Button variant="primary" loading={busy} disabled={!reason.trim() || picked.length === 0 || invalid} onClick={() => void submit()}>
            {isOwner ? 'Return and raise debit note' : 'Save draft'}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-neutral-600 dark:text-neutral-400">
          These quantities leave the stock of {locationName ?? 'the store'} at today&apos;s cost. A debit note is raised at the bill&apos;s
          price and GST. It takes the amount off the supplier&apos;s bill, or stays as credit for the next bill.
        </p>
        <div className="space-y-3">
          {lines.map((i) => {
            const max = returnableQuantity(i);
            const tracked = (i.serial_numbers?.length ?? 0) > 0;
            return (
              <div key={i.id} className="space-y-1">
                <div className="flex items-center justify-between gap-3 text-sm">
                  <span className="text-neutral-700 dark:text-neutral-200">
                    {i.item_name}{' '}
                    <span className="text-xs text-neutral-500">
                      · {max} {i.unit} can go back{(i.returned_quantity ?? 0) > 0 ? ` (${i.returned_quantity} returned)` : ''}
                    </span>
                  </span>
                  <div className="w-28">
                    <Input type="number" min={0} max={max} value={qty[i.id] ?? ''} disabled={max <= 0}
                      onChange={(v) => setQty((q) => ({ ...q, [i.id]: v }))} aria-label={`Quantity of ${i.item_name} to return`} />
                  </div>
                </div>
                {tracked && Number(qty[i.id]) > 0 && (
                  <Input value={serials[i.id] ?? ''} onChange={(v) => setSerials((s) => ({ ...s, [i.id]: v }))}
                    placeholder={`Serial numbers going back (${qty[i.id]})`} aria-label={`Serial numbers of ${i.item_name} to return`} />
                )}
              </div>
            );
          })}
        </div>
        <Input value={reason} onChange={setReason} placeholder="Why are these going back? (e.g. damaged in transit)" />
      </div>
    </Modal>
  );
}
